<template>
  <b-modal
    id="modal-topup"
    centered
    title="Add Top-Up"
    hide-footer
    @hidden="resetModal"
    no-close-on-backdrop
    size="md"
  >
    <validation-observer ref="topupValidation">
      <b-form v-on:submit.prevent="validationForm">
        <b-form-group label="Amount" label-for="amount" class="required-label">
          <validation-provider #default="{ errors }" name="Amount" vid="amount" rules="required|numeric|min:0.01">
            <b-form-input
              id="amount"
              type="number"
              step="0.01"
              v-model="topupAmount"
              :state="errors.length > 0 ? false : null"
              placeholder="0.00"
              @input="calculatePerPostCost"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <b-form-group label="Currency" label-for="currency" class="required-label">
          <validation-provider #default="{ errors }" name="Currency" vid="currency_id" rules="required">
            <v-select
              id="currency"
              v-model="selectedCurrency"
              :options="filteredCurrencyOptions"
              :reduce="(item) => item.id"
              label="currency"
              placeholder="Select Currency"
              @input="calculatePerPostCost"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <b-form-group label="Posts Added" label-for="posts_added" class="required-label">
          <validation-provider #default="{ errors }" name="Posts Added" vid="posts_added" rules="required|integer|min:1">
            <b-form-input
              id="posts_added"
              type="number"
              v-model="topupPosts"
              :state="errors.length > 0 ? false : null"
              placeholder="0"
              @input="calculatePerPostCost"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <b-form-group label="Top Up Date" label-for="topup_date" class="required-label">
          <b-form-datepicker
            id="topup_date"
            v-model="topupDate"
            :max="todayDateString"
            locale="en-US"
            placeholder="Select Date"
            today-button
            close-button
            reset-button
          />
        </b-form-group>

        <b-form-group v-if="calculatedCost > 0" label="Per Post Cost (Preview)">
          <b-form-input :value="currencySymbol + calculatedCost" disabled />
        </b-form-group>

        <b-form-group label="Attachment" label-for="attachment">
          <b-form-file
            id="attachment"
            v-model="attachmentFiles"
            multiple
            placeholder="Choose files or drop here..."
            drop-placeholder="Drop files here..."
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
          />
        </b-form-group>

        <b-form-group label="Note" label-for="note">
          <b-form-textarea
            id="note"
            v-model="topupNote"
            placeholder="Optional note"
            rows="2"
          />
        </b-form-group>

        <template v-if="isSubmitLoading">
          <b-button class="float-right" variant="primary" disabled>
            <b-spinner small />
            Loading...
          </b-button>
        </template>
        <template v-else>
          <b-button
            type="submit"
            class="float-right"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
          >
            Submit
          </b-button>
        </template>
      </b-form>
    </validation-observer>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormDatepicker,
  BFormGroup,
  BFormInput,
  BFormFile,
  BFormTextarea,
  BButton,
  BSpinner,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "@validations";
import Ripple from "vue-ripple-directive";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: "TopUpModal",
  components: {
    BModal,
    BForm,
    BFormDatepicker,
    BFormGroup,
    BFormInput,
    BFormFile,
    BFormTextarea,
    BButton,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  props: {
    authorId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      topupAmount: "",
      topupPosts: "",
      topupDate: (() => {
        const d = new Date();
        const m = `${d.getMonth() + 1}`.padStart(2, "0");
        const day = `${d.getDate()}`.padStart(2, "0");
        return `${d.getFullYear()}-${m}-${day}`;
      })(),
      topupNote: "",
      attachmentFiles: [],
      selectedCurrency: null,
      filteredCurrencyOptions: [],
      isSubmitLoading: false,
      calculatedCost: 0,
    };
  },
  computed: {
    currencySymbol() {
      const found = this.filteredCurrencyOptions.find((c) => c.id === this.selectedCurrency);
      return found ? found.currency + " " : "";
    },
    todayDateString() {
      const d = new Date();
      const m = `${d.getMonth() + 1}`.padStart(2, "0");
      const day = `${d.getDate()}`.padStart(2, "0");
      return `${d.getFullYear()}-${m}-${day}`;
    },
  },
  async mounted() {
    await this.loadCurrencies();
  },
  methods: {
    async loadCurrencies() {
      try {
        const response = await this.$api.get("api/currencies/all");
        this.filteredCurrencyOptions = response?.data?.data || [];
      } catch (error) {
        // silent
      }
    },

    calculatePerPostCost() {
      const amount = parseFloat(this.topupAmount);
      const posts = parseInt(this.topupPosts, 10);
      if (amount > 0 && posts > 0) {
        this.calculatedCost = (amount / posts).toFixed(2);
      } else {
        this.calculatedCost = 0;
      }
    },

    resetModal() {
      this.topupDate = this.todayDateString;
      this.topupAmount = "";
      this.topupPosts = "";
      this.topupNote = "";
      this.attachmentFiles = [];
      this.selectedCurrency = null;
      this.calculatedCost = 0;
    },

    validationForm: async function () {
      this.$refs.topupValidation.validate().then(async (success) => {
        if (success) {
          try {
            this.isSubmitLoading = true;

            const formData = new FormData();
            formData.append("amount", this.topupAmount);
            formData.append("posts_added", this.topupPosts);
            formData.append("topup_date", this.topupDate);
            formData.append("currency_id", this.selectedCurrency);
            if (this.topupNote) formData.append("note", this.topupNote);
            if (this.attachmentFiles && this.attachmentFiles.length) {
              for (let i = 0; i < this.attachmentFiles.length; i++) {
                formData.append("attachments[]", this.attachmentFiles[i]);
              }
            }
            await this.$api.post(
              `/api/outreach-authors/${this.authorId}/topup`,
              formData,
              { headers: { "Content-Type": "multipart/form-data" } }
            );

            this.isSubmitLoading = false;
            this.$bvModal.hide("modal-topup");
            this.$emit("topup-created");

            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Success",
                icon: "BellIcon",
                variant: "success",
                text: "Top-Up Submitted Successfully",
              },
            });
          } catch (error) {
            this.isSubmitLoading = false;
            if (error?.response?.data?.message) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Error",
                  icon: "BellIcon",
                  variant: "danger",
                  text: error?.response?.data?.message,
                },
              });
            }
            if (error?.response?.data?.errors) {
              this.$refs.topupValidation.setErrors(error?.response?.data?.errors);
            }
          }
        }
      });
    },
  },
};
</script>
